import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of} from "rxjs";
import {Auth} from '@aws-amplify/auth';
import {CONFIG} from "../../config";
import {fromPromise} from "rxjs/internal-compatibility";
import {catchError, map, tap} from "rxjs/operators";
import {Router} from "@angular/router";
import {DatePipe} from "@angular/common";

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public loggedIn: BehaviorSubject<boolean>;
  public authorized: BehaviorSubject<boolean>;
  public internal: BehaviorSubject<boolean>;
  public authorizedToTranslate: BehaviorSubject<boolean>;

  constructor(
    private router: Router,
    public datePipe: DatePipe
    ) {
    this.loggedIn = new BehaviorSubject<boolean>(false);
    this.authorized = new BehaviorSubject<boolean>(false);
    this.internal = new BehaviorSubject<boolean>(false);
    this.authorizedToTranslate = new BehaviorSubject<boolean>(false);
  }

  /** signin */
  public signInADFS(): Observable<any> {
    Auth.configure(CONFIG.amplifyADFS)
    localStorage.setItem('amplify-user-pool-id', CONFIG.amplifyADFS.Auth.userPoolId)
    return fromPromise(Auth.federatedSignIn())
      .pipe(
        tap(() => {
          this.loggedIn.next(true);
          this.authorized.next(true);
        })
      );
  }

  public signInExternal(username: string, password: string): Observable<any> {
    Auth.configure(CONFIG.amplifyExternal)
    localStorage.setItem('amplify-user-pool-id', CONFIG.amplifyExternal.Auth.userPoolId)
    return fromPromise(Auth.signIn(username, password))
      .pipe(
        tap(user => {
          if (user.challengeName !== "NEW_PASSWORD_REQUIRED") {
            if (user.attributes['custom:authorized'] === 'Yes') this.authorized.next(true);
            this.loggedIn.next(true);
          }
        })
      )
  }

  /** get authenticate state */
  public isAuthenticated(): Observable<boolean> {
    return fromPromise(Auth.currentAuthenticatedUser())
      .pipe(
        map(result => {
          this.loggedIn.next(true);
          return true;
        }),
        catchError(error => {
          this.loggedIn.next(false);
          return of(false);
        })
      );
  }

  public isAuthorized(): Observable<boolean> {
    return fromPromise(Auth.currentAuthenticatedUser())
      .pipe(
        map(user => {
          if (user.pool.userPoolId !== CONFIG.amplifyADFS.Auth.userPoolId) {

            const now: string = this.datePipe.transform(new Date(), 'yyyy-MM-dd HH:mm:ss', 'UTC')!
            if (user.attributes['custom:authorized'] != 'No' && user.attributes['custom:authorized'] > now) {
              this.authorized.next(true);
              return true;
            } else {
              this.authorized.next(false);
              return false;
            }
          }
          this.authorized.next(true);
          return true;
        }),
        catchError(error => {
          this.authorized.next(false);
          return of(false);
        })
      );
  }

// auth.service.ts example:
  public isAuthorizedToTranslate(): Observable<boolean> {
    return fromPromise(Auth.currentAuthenticatedUser()).pipe(
      map((user) => {
        if (
          user.signInUserSession.idToken.payload['cognito:groups']
        ) {
          this.authorizedToTranslate.next(true);
          return true;
        }
        this.authorizedToTranslate.next(false);
        return false;
      }),
      catchError(() => {
        this.authorizedToTranslate.next(false);
        return of(false);
      })
    );
  }


  public isInternal(): Observable<boolean> {
    return fromPromise(Auth.currentAuthenticatedUser())
      .pipe(
        map(user => {
          if (user.pool.userPoolId === CONFIG.amplifyADFS.Auth.userPoolId) {
            this.internal.next(true);
            return true
          }
          this.internal.next(false);
          return false;
        }),
        catchError(error => {
          this.internal.next(false);
          return of(false);
        })
      );
  }


  /** signout */
  public signOut() {
    fromPromise(Auth.signOut())
      .subscribe(
        result => {
          localStorage.removeItem('amplify-user-pool-id')
          this.loggedIn.next(false);
          this.authorized.next(false);
          this.router.navigate(["/"]).then(
            () => window.location.reload()
          )
        },
        error => console.log(error)
      );
  }

  public completeNewPassword(user: any, newPassword: string) {
    return fromPromise(Auth.completeNewPassword(user, newPassword))
      .pipe(
        tap(user => {
          if (user.challengeParam.userAttributes['custom:authorized'] === 'Yes') this.authorized.next(true);
          this.loggedIn.next(true)
        })
      )
  }

  public forgotPassword(username: string) {
    Auth.configure(CONFIG.amplifyExternal)
    localStorage.setItem('amplify-user-pool-id', CONFIG.amplifyExternal.Auth.userPoolId)
    return fromPromise(Auth.forgotPassword(username))
  }

  forgotPasswordSubmit(username: string, code: string, password: string) {
    Auth.configure(CONFIG.amplifyExternal)
    localStorage.setItem('amplify-user-pool-id', CONFIG.amplifyExternal.Auth.userPoolId)
    return fromPromise(Auth.forgotPasswordSubmit(username, code, password))
  }
}
